import { useEffect, useState } from "react";
import { Paperclip, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";

const PERIODS = [1, 2, 3, 4, 5, 6, 7, 8];
const EMPTY_LESSON_SLOTS = [];
const AUTO_TECHNICIAN = "__auto_technician__";
const UNASSIGNED_TECHNICIAN = "__unassigned_technician__";
const blankEquipment = (qty = "") => ({
  item: "",
  qty,
  normal_qty: qty,
  units: "",
});
const blankRepeatSlot = () => ({ date: "", period: "1" });

const sourceEquipment = (source) => {
  const sourceRows = source?.booking_equipment?.length
    ? source.booking_equipment
    : source?.requirements?.length
      ? source.requirements
      : source?.equipment || [];
  const rows = sourceRows
    .filter((item) => item.item)
    .map((item) => {
      const normalQty = String(item.qty_per_group ?? item.qty_fixed ?? item.required ?? 1);
      return {
        item: item.item,
        qty: normalQty,
        normal_qty: normalQty,
        units: item.units || "",
        per_class: Boolean(item.per_class || item.per_group === null),
      };
    });
  return rows.length ? rows : [blankEquipment()];
};

const totalRequired = (row, groups) => {
  const quantity = Number(row.qty);
  const groupCount = Number(groups);
  if (!Number.isFinite(quantity) || !Number.isFinite(groupCount)) {
    return null;
  }
  const total = row.per_class ? quantity : quantity * Math.max(1, groupCount);
  return Number.isInteger(total) ? String(total) : total.toFixed(2).replace(/\.00$/, "");
};

const initialForm = (sourceBooking = null, initialPractical = null) => ({
  title: "",
  subject: "",
  class_id: "",
  groups: "1",
  lab_id: "",
  date: "",
  period: "1",
  double_period: false,
  info: "",
  equipment: [blankEquipment()],
  is_demo: false,
  save_to_library: false,
  repeat_slots: [],
  teacher_id: "",
  technician_id: AUTO_TECHNICIAN,
  existing_practical_id: initialPractical?.id || "",
  ...(sourceBooking
    ? {
        title: sourceBooking.practical?.name || "",
        subject: sourceBooking.practical?.subject || "",
        info: sourceBooking.practical?.prep_notes || "",
        equipment: sourceEquipment(sourceBooking),
      }
    : {}),
  ...(initialPractical
    ? {
        title: initialPractical.name || "",
        subject: initialPractical.subject || "",
        info: initialPractical.technician_notes || "",
        equipment: sourceEquipment(initialPractical),
      }
    : {}),
});

export function ManualBookingDialog({
  open,
  onOpenChange,
  onBooked,
  selectedLessonSlots = EMPTY_LESSON_SLOTS,
  initialBooking = null,
  initialPractical = null,
}) {
  const { user } = useAuth();
  const [form, setForm] = useState(initialForm());
  const [labs, setLabs] = useState([]);
  const [classes, setClasses] = useState([]);
  const [defaultGroupSize, setDefaultGroupSize] = useState(3);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [selectedSuggestionTitle, setSelectedSuggestionTitle] = useState("");
  const [selectedLessons, setSelectedLessons] = useState([]);
  const [selectionError, setSelectionError] = useState("");
  const [bookingOptions, setBookingOptions] = useState(null);
  const [autoTechnician, setAutoTechnician] = useState(null);
  const [automaticAssignmentLabel, setAutomaticAssignmentLabel] = useState("");
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [inventorySuggestions, setInventorySuggestions] = useState({});
  const [inventorySuggestionsLoading, setInventorySuggestionsLoading] = useState({});
  const [chosenInventoryNames, setChosenInventoryNames] = useState({});
  const [editingEquipmentIndex, setEditingEquipmentIndex] = useState(null);
  const bookingFromTimetable = selectedLessonSlots.length > 0;
  const selectedLessonSlotKey = JSON.stringify(selectedLessonSlots);
  const equipmentSearches = form.equipment
    .map((row) => row.item.trim())
    .join("\u0001");
  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };
  const choosePeriod = (period) => {
    setForm((current) => ({
      ...current,
      period,
      double_period: Number(period) >= PERIODS.length ? false : current.double_period,
    }));
  };
  const toggleDemo = (enabled) => {
    setForm((current) => ({
      ...current,
      is_demo: enabled,
      groups: enabled ? "1" : current.groups,
      equipment: enabled
        ? current.equipment.map((row) => ({
          ...row,
          normal_qty: row.normal_qty || row.qty || "1",
          qty: "1",
        }))
        : current.equipment.map((row) => ({
          ...row,
          qty: row.normal_qty || row.qty || "1",
        })),
    }));
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    setForm(initialForm(initialBooking, initialPractical));
    setFiles([]);
    setSelectedLessons([]);
    setSelectionError("");
    setSuggestions([]);
    setSelectedSuggestionTitle(initialPractical?.name || initialBooking?.practical?.name || "");
    setBookingOptions(null);
    setAutoTechnician(null);
    setAutomaticAssignmentLabel("");
    setAssignmentLoading(false);
    setInventorySuggestions({});
    setInventorySuggestionsLoading({});
    setChosenInventoryNames({});
    setEditingEquipmentIndex(null);

    api
      .get("/bookings/manual-options")
      .then((response) => {
        setBookingOptions(response.data);
        setForm((current) => ({
          ...current,
          teacher_id: response.data.default_teacher_id || "",
          technician_id: AUTO_TECHNICIAN,
        }));
      })
      .catch(() => setBookingOptions(null));

    if (!bookingFromTimetable) {
      Promise.all([api.get("/rooms"), api.get("/timetable/meta")])
        .then(([rooms, timetable]) => {
          setLabs(
            rooms.data.filter((room) => (room.kind || "lab") === "lab"),
          );
          setClasses(timetable.data.classes || []);
          setDefaultGroupSize(timetable.data.default_group_size || 3);
        })
        .catch(() => {
          setLabs([]);
          setClasses([]);
        });
      return;
    }

    api
      .post("/timetable/booking-slots", {
        lesson_slots: JSON.parse(selectedLessonSlotKey),
      })
      .then((response) => {
        const lessons = response.data.lessons || [];
        const subjects = [
          ...new Set(lessons.map((lesson) => lesson.subject).filter(Boolean)),
        ];
        setSelectedLessons(lessons);
        const assignedTechnicians = [
          ...new Map(
            lessons
              .map((lesson) => lesson.technician)
              .filter(Boolean)
              .map((technician) => [technician.id, technician]),
          ).values(),
        ];
        if (assignedTechnicians.length === 1) {
          setAutoTechnician(assignedTechnicians[0]);
          setAutomaticAssignmentLabel("");
        } else if (assignedTechnicians.length > 1) {
          setAutoTechnician(null);
          setAutomaticAssignmentLabel("Assigned automatically for each selected lesson");
        }
        if (subjects.length === 1) {
          setForm((current) => ({ ...current, subject: subjects[0] }));
        }
      })
      .catch((error) => setSelectionError(errMsg(error)));
  }, [bookingFromTimetable, initialBooking, initialPractical, open, selectedLessonSlotKey]);

  useEffect(() => {
    if (
      !open
      || !bookingOptions
      || form.technician_id !== AUTO_TECHNICIAN
      || bookingFromTimetable
    ) {
      return undefined;
    }
    if (!form.date || !form.period || !form.teacher_id) {
      setAutoTechnician(null);
      setAutomaticAssignmentLabel("");
      setAssignmentLoading(false);
      return undefined;
    }
    let active = true;
    setAssignmentLoading(true);
    api
      .post("/bookings/manual-assignment", {
        teacher_id: form.teacher_id,
        lab_id: form.lab_id || null,
        date: form.date,
        period: Number(form.period),
      })
      .then((response) => {
        if (active) {
          setAutoTechnician(response.data.technician || null);
          setAutomaticAssignmentLabel("");
        }
      })
      .catch(() => {
        if (active) {
          setAutoTechnician(null);
          setAutomaticAssignmentLabel("");
        }
      })
      .finally(() => {
        if (active) {
          setAssignmentLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [
    bookingFromTimetable,
    bookingOptions,
    form.date,
    form.lab_id,
    form.period,
    form.teacher_id,
    form.technician_id,
    open,
  ]);

  useEffect(() => {
    const query = form.title.trim();
    if (!open || !query || query === selectedSuggestionTitle) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return undefined;
    }
    let active = true;
    const search = window.setTimeout(() => {
      setSuggestionsLoading(true);
      api
        .get("/practicals", { params: { q: query } })
        .then((response) => {
          if (active) {
            setSuggestions(
              [...(response.data || [])]
                .sort((first, second) => Number(second.starred) - Number(first.starred))
                .slice(0, 6),
            );
          }
        })
        .catch(() => {
          if (active) {
            setSuggestions([]);
          }
        })
        .finally(() => {
          if (active) {
            setSuggestionsLoading(false);
          }
        });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(search);
    };
  }, [form.title, open, selectedSuggestionTitle]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const requestedRows = form.equipment
      .map((row, index) => ({ index, query: row.item.trim() }))
      .filter(({ index, query }) => (
        index === editingEquipmentIndex
        && query.length >= 2
        && chosenInventoryNames[index] !== query
      ));
    if (!requestedRows.length) {
      setInventorySuggestions({});
      setInventorySuggestionsLoading({});
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      setInventorySuggestionsLoading(
        Object.fromEntries(requestedRows.map(({ index }) => [index, true])),
      );
      Promise.all(
        requestedRows.map(async ({ index, query }) => {
          const response = await api.get("/inventory", { params: { q: query } });
          return { index, items: (response.data || []).slice(0, 6) };
        }),
      )
        .then((results) => {
          if (!active) return;
          setInventorySuggestions(
            Object.fromEntries(results.map(({ index, items }) => [index, items])),
          );
        })
        .catch(() => {
          if (active) setInventorySuggestions({});
        })
        .finally(() => {
          if (active) setInventorySuggestionsLoading({});
        });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [chosenInventoryNames, editingEquipmentIndex, equipmentSearches, form.equipment, open]);

  const updateEquipment = (index, key, value) => {
    setForm((current) => ({
      ...current,
      equipment: current.equipment.map((row, rowIndex) =>
        rowIndex === index
          ? {
            ...row,
            [key]: value,
            ...(key === "qty" && !current.is_demo ? { normal_qty: value } : {}),
          }
          : row,
      ),
    }));
    if (key === "item") {
      setChosenInventoryNames((current) => {
        const next = { ...current };
        delete next[index];
        return next;
      });
    }
  };
  const chooseInventoryEquipment = (index, item) => {
    setForm((current) => ({
      ...current,
      equipment: current.equipment.map((row, rowIndex) => (
        rowIndex === index
          ? { ...row, item: item.name, units: row.units || item.units || "" }
          : row
      )),
    }));
    setChosenInventoryNames((current) => ({ ...current, [index]: item.name }));
    setInventorySuggestions((current) => ({ ...current, [index]: [] }));
    setEditingEquipmentIndex(null);
  };
  const addEquipment = () => {
    setForm((current) => ({
      ...current,
      equipment: [...current.equipment, blankEquipment(current.is_demo ? "1" : "")],
    }));
  };
  const removeEquipment = (index) => {
    setForm((current) => ({
      ...current,
      equipment: current.equipment.filter((_, rowIndex) => rowIndex !== index),
    }));
  };
  const addRepeatSlot = () => {
    setForm((current) => ({
      ...current,
      repeat_slots: [...current.repeat_slots, blankRepeatSlot()],
    }));
  };
  const updateRepeatSlot = (index, key, value) => {
    setForm((current) => ({
      ...current,
      repeat_slots: current.repeat_slots.map((slot, slotIndex) => (
        slotIndex === index ? { ...slot, [key]: value } : slot
      )),
    }));
  };
  const removeRepeatSlot = (index) => {
    setForm((current) => ({
      ...current,
      repeat_slots: current.repeat_slots.filter((_, slotIndex) => slotIndex !== index),
    }));
  };
  const chooseClass = (classId) => {
    const selectedClass = classes.find((schoolClass) => schoolClass.id === classId);
    const pupils = selectedClass?.size || defaultGroupSize;
    const suggestedGroups = Math.max(1, Math.ceil(pupils / defaultGroupSize));
    setForm((current) => ({
      ...current,
      class_id: classId,
      groups: String(suggestedGroups),
      equipment: current.is_demo
        ? current.equipment.map((row) => ({
          ...row,
          qty: row.normal_qty || row.qty || "1",
        }))
        : current.equipment,
    }));
  };
  const choosePractical = (practical) => {
    const equipment = (practical.equipment || [])
      .filter((item) => item.item)
      .map((item) => {
        const normalQty = String(item.qty_per_group ?? item.qty_fixed ?? item.qty ?? 1);
        return {
          item: item.item,
          qty: form.is_demo ? "1" : normalQty,
          normal_qty: normalQty,
          units: item.units || "",
          per_class: Boolean(item.per_class),
        };
      });
    setForm((current) => ({
      ...current,
      title: practical.name || current.title,
      subject: practical.subject || current.subject,
      equipment: equipment.length ? equipment : [blankEquipment()],
      existing_practical_id: practical.id || "",
    }));
    setSuggestions([]);
    setSelectedSuggestionTitle(practical.name || "");
  };

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("Give the practical a title.");
      return;
    }
    if (bookingFromTimetable && (selectionError || selectedLessons.length === 0)) {
      toast.error(
        selectionError || "The selected timetable lessons are still loading.",
      );
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (!bookingFromTimetable && !form.date) {
      toast.error("Choose a date.");
      return;
    }
    if (!bookingFromTimetable && !form.class_id) {
      toast.error("Choose a class.");
      return;
    }
    if (!bookingFromTimetable && form.date < today) {
      toast.error("Choose a date that isn't in the past.");
      return;
    }
    const selectedSlots = new Set([`${form.date}|${form.period}`]);
    for (const repeatSlot of form.repeat_slots) {
      if (!repeatSlot.date) {
        toast.error("Choose a date for every repeat lesson.");
        return;
      }
      if (repeatSlot.date < today) {
        toast.error("A repeat date can't be in the past.");
        return;
      }
      const slotKey = `${repeatSlot.date}|${repeatSlot.period}`;
      if (selectedSlots.has(slotKey)) {
        toast.error("Each repeat lesson must use a different date or period.");
        return;
      }
      selectedSlots.add(slotKey);
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        subject: form.subject || null,
        class_id: form.class_id || null,
        groups: parseInt(form.groups, 10) || 1,
        lab_id: form.lab_id || null,
        date: bookingFromTimetable ? null : form.date,
        period: bookingFromTimetable ? null : parseInt(form.period, 10) || 1,
        double_period: !bookingFromTimetable && form.double_period,
        info: form.info,
        is_demo: form.is_demo,
        save_to_library: form.save_to_library,
        equipment: form.equipment
          .filter((row) => row.item.trim())
          .map((row) => ({
            item: row.item.trim(),
            qty: parseFloat(row.qty) || 1,
            units: row.units,
            per_class: Boolean(row.per_class),
          })),
        repeat_slots: form.repeat_slots.map((slot) => ({
          date: slot.date,
          period: parseInt(slot.period, 10) || 1,
        })),
        timetable_lesson_slots: bookingFromTimetable ? selectedLessonSlots : [],
        teacher_id: form.teacher_id || null,
        technician_id: form.technician_id === AUTO_TECHNICIAN
          || form.technician_id === UNASSIGNED_TECHNICIAN
          ? null
          : form.technician_id,
        technician_override: Boolean(
          bookingOptions?.can_choose_technician
          && form.technician_id !== AUTO_TECHNICIAN,
        ),
        existing_practical_id: form.existing_practical_id || null,
      };
      const { data } = await api.post("/bookings/manual", payload);
      const practicalId = data.bookings?.[0]?.practical_id;
      let failedFiles = 0;
      for (const file of files) {
        const attachment = new FormData();
        attachment.append("file", file);
        attachment.append("title", file.name);
        attachment.append("category", "Practical instructions");
        if (form.save_to_library && practicalId) {
          attachment.append("practical_id", practicalId);
        }
        try {
          await api.post("/documents", attachment);
        } catch {
          failedFiles += 1;
        }
      }
      const bookingMessage =
        data.count > 1
          ? `${data.count} bookings sent to the prep room.`
          : `Booking ${data.bookings[0].reference} sent to the prep room.`;
      toast.success(bookingMessage);
      if (failedFiles) {
        toast.warning(
          `${failedFiles} attachment${failedFiles === 1 ? "" : "s"} couldn't be uploaded.`,
        );
      }
      onOpenChange(false);
      onBooked?.(data.bookings || []);
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        data-testid="manual-booking-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {bookingFromTimetable
              ? `Book one practical for ${selectedLessonSlots.length} lesson${
                  selectedLessonSlots.length === 1 ? "" : "s"
                }`
              : "Book a practical manually"}
          </DialogTitle>
          <p
            className="text-sm text-muted-foreground"
            data-testid="manual-booking-dialog-description"
          >
            This goes straight to your technician's prep queue — no approval needed.
          </p>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {initialBooking && !bookingFromTimetable && (
            <div
              className="rounded-lg border bg-muted/30 px-3 py-2 text-sm"
              data-testid="repeat-booking-source-summary"
            >
              Repeating {initialBooking.practical?.name || "this practical"} from{
                " "
              }
              {initialBooking.reference}. Choose your own class, date and period below.
            </div>
          )}
          {bookingFromTimetable && (
            <div
              className="space-y-2 rounded-lg border bg-muted/30 p-3"
              data-testid="timetable-booking-selected-lessons"
            >
              <p className="text-sm font-medium">Selected upcoming lessons</p>
              {selectionError ? (
                <p
                  className="text-sm text-destructive"
                  data-testid="timetable-booking-selection-error"
                >
                  {selectionError}
                </p>
              ) : selectedLessons.length === 0 ? (
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="timetable-booking-selection-loading"
                >
                  Checking the next scheduled lessons…
                </p>
              ) : (
                <ul
                  className="space-y-1 text-sm"
                  data-testid="timetable-booking-selected-lessons-list"
                >
                  {selectedLessons.map((lesson) => (
                    <li
                      key={`${lesson.entry_id}-${lesson.date}`}
                      data-testid={`timetable-booking-lesson-${lesson.entry_id}-${lesson.date}`}
                    >
                      {lesson.class_name} · {lesson.suggested_groups} group
                      {lesson.suggested_groups === 1 ? "" : "s"} · {fmtDate(lesson.date)} · P
                      {lesson.period}
                      {lesson.lab ? ` · ${lesson.lab}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>Teacher</Label>
              <Select
                value={form.teacher_id}
                disabled={!bookingOptions?.can_choose_teacher || bookingFromTimetable}
                onValueChange={(value) => updateForm("teacher_id", value)}
              >
                <SelectTrigger data-testid="manual-booking-teacher">
                  <SelectValue placeholder="Choose teacher" />
                </SelectTrigger>
                <SelectContent>
                  {(bookingOptions?.teachers || []).map((teacher) => (
                    <SelectItem
                      key={teacher.id}
                      value={teacher.id}
                      data-testid={`manual-booking-teacher-option-${teacher.id}`}
                    >
                      {teacher.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Technician</Label>
              <Select
                value={form.technician_id}
                disabled={!bookingOptions?.can_choose_technician}
                onValueChange={(value) => updateForm("technician_id", value)}
              >
                <SelectTrigger data-testid="manual-booking-technician">
                  <SelectValue placeholder="Checking assignment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value={AUTO_TECHNICIAN}
                    data-testid="manual-booking-technician-option-automatic"
                  >
                    {assignmentLoading
                      ? "Checking automatic assignment…"
                      : autoTechnician
                        ? `Assigned automatically: ${autoTechnician.name}`
                        : automaticAssignmentLabel
                          ? automaticAssignmentLabel
                        : "Unassigned (no automatic match)"}
                  </SelectItem>
                  {bookingOptions?.can_choose_technician && (
                    <SelectItem
                      value={UNASSIGNED_TECHNICIAN}
                      data-testid="manual-booking-technician-option-unassigned"
                    >
                      Leave unassigned
                    </SelectItem>
                  )}
                  {(bookingOptions?.technicians || []).map((technician) => (
                    <SelectItem
                      key={technician.id}
                      value={technician.id}
                      data-testid={`manual-booking-technician-option-${technician.id}`}
                    >
                      {technician.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="relative">
            <Label htmlFor="manual-booking-title">Title</Label>
            <Input
              id="manual-booking-title"
              value={form.title}
              onChange={(event) => {
                setSelectedSuggestionTitle("");
                setForm((current) => ({
                  ...current,
                  title: event.target.value,
                  existing_practical_id: "",
                }));
              }}
              placeholder="e.g. Required practical: osmosis in potato"
              data-testid="manual-booking-title"
            />
            {(suggestionsLoading || suggestions.length > 0) && (
              <div
                className={
                  "absolute z-20 mt-1 w-full overflow-hidden rounded-md border "
                  + "bg-popover shadow-lg"
                }
                data-testid="manual-booking-practical-suggestions"
              >
                {suggestionsLoading ? (
                  <p
                    className="px-3 py-2 text-sm text-muted-foreground"
                    data-testid="manual-booking-practical-suggestions-loading"
                  >
                    Searching practical library…
                  </p>
                ) : (
                  suggestions.map((practical) => (
                    <button
                      key={practical.id}
                      type="button"
                      onClick={() => choosePractical(practical)}
                      className={
                        "block w-full border-b px-3 py-2 text-left last:border-0 "
                        + "hover:bg-muted"
                      }
                      data-testid={`manual-booking-practical-suggestion-${practical.id}`}
                    >
                      <span className="block text-sm font-medium">{practical.name}</span>
                      <span className="block text-xs text-muted-foreground">
                        {[practical.subject, practical.topic]
                          .filter(Boolean)
                          .join(" · ")}
                        {practical.equipment?.length
                          ? ` · ${practical.equipment.length} equipment item${
                              practical.equipment.length === 1 ? "" : "s"
                            }`
                          : ""}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div
            className={`grid grid-cols-1 gap-3 ${
              bookingFromTimetable ? "" : "sm:grid-cols-3"
            }`}
          >
            <div>
              <Label>Subject</Label>
              <Select
                value={form.subject}
                onValueChange={(value) => updateForm("subject", value)}
              >
                <SelectTrigger data-testid="manual-booking-subject">
                  <SelectValue placeholder="Subject" />
                </SelectTrigger>
                <SelectContent>
                  {["Biology", "Chemistry", "Physics", "Other"].map((subject) => (
                    <SelectItem key={subject} value={subject}>
                      {subject}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!bookingFromTimetable && (
              <>
                <div>
                  <Label>Class</Label>
                  <Select value={form.class_id} onValueChange={chooseClass}>
                    <SelectTrigger data-testid="manual-booking-class">
                      <SelectValue placeholder="Choose class" />
                    </SelectTrigger>
                    <SelectContent>
                      {classes.map((schoolClass) => (
                        <SelectItem key={schoolClass.id} value={schoolClass.id}>
                          {schoolClass.name}
                          {schoolClass.size ? ` · ${schoolClass.size} pupils` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="manual-booking-groups">Groups</Label>
                  <Input
                    id="manual-booking-groups"
                    min="1"
                    type="number"
                    value={form.groups}
                    onChange={(event) => updateForm("groups", event.target.value)}
                    data-testid="manual-booking-groups"
                  />
                </div>
              </>
            )}
          </div>
          {!bookingFromTimetable && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <Label>Lab</Label>
                <Select value={form.lab_id} onValueChange={(value) => updateForm("lab_id", value)}>
                  <SelectTrigger data-testid="manual-booking-lab">
                    <SelectValue placeholder="Choose lab" />
                  </SelectTrigger>
                  <SelectContent>
                    {labs.length ? (
                      labs.map((lab) => (
                        <SelectItem key={lab.id} value={lab.id}>
                          {lab.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>
                        No labs set up
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="manual-booking-date">Date</Label>
                <Input
                  id="manual-booking-date"
                  type="date"
                  value={form.date}
                  onChange={(event) => updateForm("date", event.target.value)}
                  data-testid="manual-booking-date"
                />
              </div>
              <div>
                <Label>Period</Label>
                <Select value={form.period} onValueChange={choosePeriod}>
                  <SelectTrigger data-testid="manual-booking-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODS.map((period) => (
                      <SelectItem key={period} value={String(period)}>
                        Period {period}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="mt-2 flex items-center gap-2 text-xs">
                  <Checkbox
                    checked={form.double_period}
                    disabled={Number(form.period) >= PERIODS.length}
                    onCheckedChange={(value) => updateForm("double_period", Boolean(value))}
                    data-testid="manual-booking-double-period"
                  />
                  Double period — required in periods {form.period} and {Number(form.period) + 1}
                </label>
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="manual-booking-info">Info for the technician</Label>
            <Textarea
              id="manual-booking-info"
              value={form.info}
              onChange={(event) => updateForm("info", event.target.value)}
              placeholder="Anything the technician should know…"
              rows={2}
              data-testid="manual-booking-info"
            />
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label>Equipment</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addEquipment}
                data-testid="manual-booking-add-equipment"
              >
                <Plus className="mr-1 h-4 w-4" />
                Add row
              </Button>
            </div>
            <div className="space-y-2">
              {form.equipment.map((row, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2"
                  data-testid={`manual-booking-equipment-row-${index}`}
                >
                  <div className="relative flex-1">
                    <Input
                      placeholder="Item"
                      value={row.item}
                      onChange={(event) =>
                        updateEquipment(index, "item", event.target.value)
                      }
                      onFocus={() => setEditingEquipmentIndex(index)}
                      onBlur={() => setEditingEquipmentIndex((current) => (
                        current === index ? null : current
                      ))}
                      data-testid={`manual-booking-equipment-item-${index}`}
                    />
                    {inventorySuggestionsLoading[index] && (
                      <p
                        className="mt-1 text-xs text-muted-foreground"
                        data-testid={`manual-booking-inventory-suggestions-loading-${index}`}
                      >
                        Searching inventory…
                      </p>
                    )}
                    {inventorySuggestions[index]?.length > 0 && (
                      <div
                        className="mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-lg"
                        data-testid={`manual-booking-inventory-suggestions-${index}`}
                      >
                        {inventorySuggestions[index].map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className="block w-full border-b px-3 py-2 text-left last:border-0 hover:bg-muted"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => chooseInventoryEquipment(index, item)}
                            data-testid={`manual-booking-inventory-suggestion-${index}-${item.id}`}
                          >
                            <span className="block text-sm font-medium">{item.name}</span>
                            <span className="block text-xs text-muted-foreground">
                              {item.quantity ?? "—"} {item.units || "items"}
                              {item.location ? ` · ${item.location}` : ""}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="w-20 shrink-0">
                    <Input
                      className="w-full"
                      type="number"
                      placeholder="Qty"
                      aria-label={`Quantity per group for ${row.item || "equipment item"}`}
                      value={row.qty}
                      onChange={(event) =>
                        updateEquipment(index, "qty", event.target.value)
                      }
                      data-testid={`manual-booking-equipment-qty-${index}`}
                    />
                    <span
                      className="mt-1 block whitespace-nowrap text-xs text-muted-foreground"
                      data-testid={`manual-booking-equipment-total-${index}`}
                    >
                      {totalRequired(row, form.groups) ?? "—"} total
                    </span>
                  </div>
                  <Input
                    className="w-24"
                    placeholder="Units"
                    value={row.units}
                    onChange={(event) =>
                      updateEquipment(index, "units", event.target.value)
                    }
                    data-testid={`manual-booking-equipment-units-${index}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeEquipment(index)}
                    disabled={form.equipment.length === 1}
                    data-testid={`manual-booking-equipment-remove-${index}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Enter quantity per group. The total needed updates as group count changes.
            </p>
          </div>
          <div>
            <Label className="flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              Attachments
            </Label>
            <Input
              type="file"
              multiple
              onChange={(event) => setFiles(Array.from(event.target.files || []))}
              className="mt-1"
              data-testid="manual-booking-attachments"
            />
            {files.length > 0 && (
              <p
                className="mt-1 text-xs text-muted-foreground"
                data-testid="manual-booking-file-count"
              >
                {files.length} file{files.length === 1 ? "" : "s"} selected
              </p>
            )}
          </div>
          <div className="space-y-3 rounded-lg border p-3">
            <label className="flex items-center gap-3 text-sm">
              <Checkbox
                checked={form.is_demo}
                onCheckedChange={(value) => toggleDemo(Boolean(value))}
                data-testid="manual-booking-demo"
              />
              This is a demo booking
            </label>
            <label className="flex items-center gap-3 text-sm">
              <Checkbox
                checked={form.save_to_library}
                onCheckedChange={(value) => updateForm("save_to_library", Boolean(value))}
                data-testid="manual-booking-save-library"
              />
              Save to the Practical library for reuse
            </label>
            {!bookingFromTimetable && (
              <label className="flex items-center gap-3 text-sm">
                <Checkbox
                  checked={form.repeat_slots.length > 0}
                  onCheckedChange={(value) => {
                    updateForm("repeat_slots", value ? [blankRepeatSlot()] : []);
                  }}
                  data-testid="manual-booking-repeat"
                />
                Repeat this practical on another lesson
              </label>
            )}
            {form.repeat_slots.length > 0 && !bookingFromTimetable && (
              <div className="space-y-3 pl-7">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Repeat lessons</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addRepeatSlot}
                    disabled={form.repeat_slots.length >= 19}
                    data-testid="manual-booking-add-repeat-date"
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add date
                  </Button>
                </div>
                {form.repeat_slots.map((repeatSlot, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]"
                    data-testid={`manual-booking-repeat-row-${index}`}
                  >
                    <div>
                      <Label htmlFor={`manual-booking-repeat-date-${index}`}>
                        Repeat date
                      </Label>
                      <Input
                        id={`manual-booking-repeat-date-${index}`}
                        type="date"
                        value={repeatSlot.date}
                        onChange={(event) =>
                          updateRepeatSlot(index, "date", event.target.value)
                        }
                        data-testid={`manual-booking-repeat-date-${index}`}
                      />
                    </div>
                    <div>
                      <Label>Repeat period</Label>
                      <Select
                        value={repeatSlot.period}
                        onValueChange={(value) =>
                          updateRepeatSlot(index, "period", value)
                        }
                      >
                        <SelectTrigger data-testid={`manual-booking-repeat-period-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PERIODS.map((period) => (
                            <SelectItem key={period} value={String(period)}>
                              Period {period}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="justify-self-end self-end"
                      onClick={() => removeRepeatSlot(index)}
                      aria-label={`Remove repeat lesson ${index + 1}`}
                      data-testid={`manual-booking-remove-repeat-date-${index}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="manual-booking-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || (bookingFromTimetable && !selectedLessons.length)}
            data-testid="manual-booking-submit"
          >
            {saving
              ? "Booking…"
              : bookingFromTimetable
                ? "Book selected lessons"
                : "Book it"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}